import {
  useVuelidate,
  type ValidationArgs,
  type ValidationRule,
} from "@vuelidate/core";
import { helpers, required, requiredIf } from "@vuelidate/validators";
import type { Ref, ToRefs } from "vue";
import { EMAIL_RG_PATTERN, PASSWORD_RG_PATTERN } from "@/utils/misc.utils";

export type RulesProps = {
  validator: (value: string) => boolean;
  message: string;
};

const validators = {
  required: helpers.withMessage("Ce champ est requis", required),
  email: helpers.withMessage(
    "Le format de l'email est incorrect",
    (value: string) => {
      return !value || new RegExp(EMAIL_RG_PATTERN).test(value);
    }
  ),
  isSlug: helpers.withMessage(
    "Le format du slug est incorrect",
    (value: string) => {
      return !value || /^[a-z0-9_]+(?:-[a-z0-9_]+)*$/.test(value);
    }
  ),
  requiredIf: (
    prop: boolean | Ref<boolean> | string | (() => boolean | Promise<boolean>)
  ) => helpers.withMessage("Ce champ est requis", requiredIf(prop)),

  password: helpers.withMessage(
    "Mot de passe trop court ou format incorrect",
    (value: string) => {
      return !value || new RegExp(PASSWORD_RG_PATTERN).test(value);
    }
  ),
  min: (min: number) =>
    helpers.withMessage(
      `Ce champs dois être supérieur à ${min}`,
      (value: number) => {
        return value >= min;
      }
    ),
  custom: (message: string, validator: ValidationRule) =>
    helpers.withMessage(message, validator),
};

export function useValidation<
  T extends { [key in keyof Vargs]: any },
  Vargs extends ValidationArgs = ValidationArgs
>(state: T | Ref<T> | ToRefs<T>, createRules: (v: typeof validators) => Vargs) {
  const v$ = useVuelidate(createRules(validators), state);

  const getMessage = (key: string) => {
    return v$.value[key]?.$errors[0]?.$message;
  };

  const check = () => {
    v$.value.$touch();
    return !v$.value.$invalid;
  };

  return {
    check,
    getMessage,
  };
}
